import React, { useState, useMemo, useEffect, useRef } from 'react';
import { buildEngine } from '../../engine/Engine';
import { buildFieldSuggestions } from '@coveo/headless';
import Dropdown from '../../../components/react/Dropdown';
import useEventBus, { dispatch } from '../../../hooks/useEventBus';
import { getLocationCookie,  setLocationCookie } from '../../helpers';
import { APIProvider, ControlPosition, MapControl,  Map, useMapsLibrary } from "@vis.gl/react-google-maps";

const API_KEY = 'AIzaSyBSYmfUjKpOk-N4c6V__k0tEs5yq5F42BU';

const LocationSelector = props => {
  const {
    locationCookieName,
    coveoOrganizationId,
    coveoAccessToken,
    coveoSearchHub,
    coveoPipeline,
    coveoAutocompleteFields,
    coveoEngine = null,
    countryOptions = [],
    careTypeOptions = [],
    distanceOptions = [],
    searchWithInfoRedirect,
    searchNoInfoRedirect,
    urlUsa = 'https://www.sunriseseniorliving.com',
    urlCanada = 'https://www.sunriseseniorliving.ca',
    careTypePlaceholder,
    distancePlaceholder,
    searchButtonLabel,
    searchBoxPlaceholder = 'Enter a zip code',
    externalSubmitHandler = null,
    onCountryChange = null,
    onCareTypeChange = null,
    onDistanceChange = null,
    allowEmptySearch = false,
    module
  } = props;

  const [, setGeolocationData] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [, setSelectedPlace] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(countryOptions.length > 0  ? JSON.parse(countryOptions).find(x => x.defaultValue === 'true') : null);
  const [selectedCareType, setSelectedCareType] = useState(careTypeOptions.length > 0 ? JSON.parse(careTypeOptions).find(x => x.defaultValue === 'true') : null);
  const [selectedDistance, setSelectedDistance] = useState(distanceOptions.length > 0   ? JSON.parse(distanceOptions).find(x => x.defaultValue === 'true') : null);

  let engine;
  if (coveoEngine === null && coveoOrganizationId && coveoAccessToken) {
    engine = useMemo(() => {
      return buildEngine({
        organizationId: coveoOrganizationId,
        accessToken: coveoAccessToken,
        searchHub: coveoSearchHub,
        pipeline: coveoPipeline
      });
    }, []);
  } else if (coveoEngine !== null) {
    engine = useMemo(() => coveoEngine, [coveoEngine]);
  }

  const PlaceAutocomplete = ({ onPlaceSelect }) => {
    const [placeAutocomplete, setPlaceAutocomplete] = useState(null);
    const inputRef = useRef(null);
    const places = useMapsLibrary("places");
  
    useEffect(() => {
      if (!places || !inputRef.current) {
        return;
      }

      const options = {
        fields: ["geometry", "name", "formatted_address"],
        types: ['(regions)'],
        componentRestrictions: {country: [selectedCountry.value]}
      };
  
      setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
    }, [places]);

    useEffect(() => {
      if (!placeAutocomplete) {
        return;
      }
  
      placeAutocomplete.addListener("place_changed", () => {
        onPlaceSelect(placeAutocomplete.getPlace());        
        setSearchInputValue(placeAutocomplete.getPlace().formatted_address);
      });
    }, [onPlaceSelect, placeAutocomplete]);

    const handleChange = (e) => {
      if (e) {
        setSearchInputValue('')
      }
    }

    const handleBlur = () => {
      inputRef.current.value = '';
      setSearchInputValue('');
    }

    return (
      <div className="autocomplete-container" >
        <input 
          defaultValue={searchInputValue}
          onChange={e => handleChange(e)}
          onBlur={e => handleBlur()}
          ref={inputRef} 
          placeholder={selectedCountry?.placeholder || searchBoxPlaceholder}
        />
      </div>
    );
  };

  let suggestionsControllersArray = [];
  if (coveoAutocompleteFields) {
    const autocompleteFieldsArray = coveoAutocompleteFields.split('|');

    autocompleteFieldsArray.forEach(field => {
      const suggestionsController = buildFieldSuggestions(engine, {
        options: {
          facet: {
            field: field,
            facetId: `${field}-facet`,
            facetSearch: {
              numberOfValues: 1
            }
          }
        }
      });
      suggestionsControllersArray.push(suggestionsController);
    });
  }

  useEventBus(
    'geolocation-received',
    ({ payload }) => {
      if (payload.Module != module) {
        return;
      }

      if (externalSubmitHandler !== null && searchWithInfoRedirect === null && searchNoInfoRedirect === null) {
        return;
      }

      let cookieData = getLocationCookie(locationCookieName);
      if (cookieData) {
        setGeolocationData(cookieData);
      }
      
      const countryRedirect = cookieData.Country === 'USA' ? urlUsa : urlCanada;
      const queryString = `?value=${cookieData.value}&country=${cookieData.country}&State=${cookieData.State}&LastSearchValue=${cookieData.LastSearchValue}&ssl_levelsofcare_01=${cookieData.ssl_levelsofcare_01}&Longitude=${cookieData.Longitude}&Latitude=${cookieData.Latitude}#f-ssl_levelsofcare_01=${cookieData.ssl_levelsofcare_01}`;

      let url = ''
      if (cookieData.State) {
        url = `${countryRedirect}${searchWithInfoRedirect}/${cookieData.State.toLowerCase()}${queryString}`;
      } else if (cookieData.Latitude && cookieData.Longitude && cookieData.LastSearchValue.length > 0) {
        url = `${countryRedirect}${searchWithInfoRedirect}${queryString}`;
      } else {
        url = `${countryRedirect}${searchNoInfoRedirect}`;
      }
      
      window.location.href = url;
    },
    []
  );

  const handleLocationQuery = (searchInputValue, selectedCountry, selectedCareType, selectedDistance) => {
    dispatch({
      type: 'get-zip-geolocation',
      payload: {
        value: searchInputValue,
        country: `${selectedCountry.value === 'us' ? 'USA' : 'CANADA'}`,
        moduleName: module
      }
    });

    const data = {
      value: searchInputValue,
      country: selectedCountry ? selectedCountry.value : null,
      ssl_levelsofcare_01: selectedCareType ? selectedCareType.value : null,
      distance: selectedDistance ? selectedDistance.value : null
    };

    setLocationCookie(data);

    return null;
  };

  const handleSubmit = () => {
    if (searchInputValue) {
      const selectedOption = selectedCareType ? selectedCareType.label : '';
      submitGoogleEvent(searchInputValue, selectedOption);

      if (externalSubmitHandler !== null) {
        externalSubmitHandler(searchInputValue, selectedCountry, selectedCareType, selectedDistance);
      } else {
        handleLocationQuery(searchInputValue, selectedCountry, selectedCareType, selectedDistance);
      }
    }
  };

  const handleCountryChange = (eventOption) => {
    let selectedOption;
    if (eventOption !== null) {
      selectedOption = JSON.parse(countryOptions).find(option => option.value === eventOption.value);
    }

    if (onCountryChange) {
      onCountryChange(selectedOption);
    }

    setSelectedCountry(selectedOption);
    setSearchInputValue('');
  };

  const handleCareTypeChange = (eventOption) => {
    let selectedOption = null;
    if (eventOption !== null) {
      selectedOption = JSON.parse(careTypeOptions).find(option => option.value === eventOption.value);
    }

    setSelectedCareType(selectedOption);
    setLocationCookie(JSON.parse('{"ssl_levelsofcare_01": "' + selectedOption.value + '"}'));
    
    if (onCareTypeChange) {
      onCareTypeChange(selectedOption);
    }
  };

  const handleDistanceChange = (eventOption) => {
    let selectedOption = null;
    if (eventOption !== null) {
      selectedOption = JSON.parse(distanceOptions).find( option => option.value === eventOption.value);
    }

    setSelectedDistance(selectedOption);

    if (onDistanceChange) onDistanceChange(selectedOption);
  };

  const submitGoogleEvent = (searchTerm, selectedOption) => {
    const eventData = {
      event: "search",
      detailed_event: "Search",
      event_data: {
        identifier: "community_search",
        name: "community_search",
        corrected_term: searchTerm,
        search_term: searchTerm,
        search_type: selectedOption
      }
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_data: null });
    window.dataLayer.push(eventData);
  };

  return (
    <>
      <div className={`location-selector-headless__container ${ careTypeOptions.length > 0 && distanceOptions.length > 0 ? 'location-selector-headless__container--three-dropdowns' : 'location-selector-headless__container--two-dropdowns'}`}>
        <div className="location-selector-headless__location-container">
          {countryOptions.length > 0 && (
            <div className="field dropdown-module location-selector__country-select">
              <div className="field__input-label-wrapper">
                <div className="field__select-arrow-wrapper">
                  <Dropdown
                    options={JSON.parse(countryOptions)}
                    selectedOption={selectedCountry}
                    onChange={handleCountryChange}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 15 15"
                    width="10"
                    height="10"
                  >
                    <path
                      d="M8.306 10.992l6.675-5.747L13.332 3.3 7.481 8.338 1.63 3.3-.019 5.245l6.675 5.747.825.708z"
                      fill="currentcolor"
                      fillRule="nonzero"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>
          )}
          <div className='location-selector-headless__input'>   
            <APIProvider apiKey={API_KEY}>
              <Map
                style={{width: '100%', visibility: 'hidden'}}
                defaultCenter={{lat: 22.54992, lng: 0}}
                defaultZoom={3}
                disableDefaultUI={true}
              />
              <MapControl position={ControlPosition.TOP_LEFT}> 
                <div className="autocomplete-control">
                  <PlaceAutocomplete onPlaceSelect={setSelectedPlace} />
                </div>
              </MapControl>
            </APIProvider>
          </div>
        </div>
        {careTypeOptions.length > 0 && (
          <div className="location-selector-headless__dropdown location-selector-headless__care-type">
            <div className="field dropdown-module">
              <div className="field__input-label-wrapper">
                <div className="field__select-arrow-wrapper">
                  <Dropdown
                    addPlaceholderOption={true}
                    selectedOption={selectedCareType}
                    placeholder={careTypePlaceholder}
                    options={JSON.parse(careTypeOptions)}
                    onChange={handleCareTypeChange}
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"  width="10" height="10">
                    <path  d="M8.306 10.992l6.675-5.747L13.332 3.3 7.481 8.338 1.63 3.3-.019 5.245l6.675 5.747.825.708z" fill="currentcolor" fillRule="nonzero"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}
        {distanceOptions.length > 0 && (
          <div className="location-selector-headless__dropdown location-selector-headless__distance">
            <div className="field dropdown-module">
              <div className="field__input-label-wrapper">
                <div className="field__select-arrow-wrapper">
                  <Dropdown
                    addPlaceholderOption={true}
                    selectedOption={selectedDistance}
                    placeholder={distancePlaceholder}
                    options={JSON.parse(distanceOptions)}
                    onChange={handleDistanceChange}
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15" width="10" height="10">
                    <path d="M8.306 10.992l6.675-5.747L13.332 3.3 7.481 8.338 1.63 3.3-.019 5.245l6.675 5.747.825.708z" fill="currentcolor" fillRule="nonzero"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}
        <button
          className="btn btn--submit location-selector-headless__search-button"
          onClick={handleSubmit}
          disabled={!searchInputValue && (!allowEmptySearch || (allowEmptySearch && allowEmptySearch === 'false'))}>
          <span className="btn__label">{searchButtonLabel}</span>
          <svg width="11" height="18" role="presentation">
            <use xlinkHref="#arrow-right"></use>
          </svg>
        </button>
      </div>
    </>
  );
};

export default LocationSelector;
